import { Search, ListFilter, Eye, Pencil, Trash2, X, Filter, Plus, User } from "lucide-react";
import { useRef, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { getDownlineAgents, deleteDownlineAgent } from "../../../services/agent_management_service";
import { useSelector } from "react-redux";
import { handleSetSearch } from "../../../utils/searchDebouncing";
import CreateAgent from "./components/createAgent";
import { useNavigate } from "react-router-dom";
import ARHCustomDataTable from "../../../components/dataTable/ARHCustomDataTable";
import ARHAdvanceFilterModal from "../../../components/application/date-picker/FilterModal";
import { ARHCustomDateFiler } from "../../../components/application/date-picker/rangeCallernderCard";
import { useTranslation } from "react-i18next";
import AssignSlabModal from "./components/AssignSlabModal";

function ManageAgent() {
  const { t } = useTranslation();
  const searchRef = useRef(null);
  const userInfo = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [refetch, setRefetch] = useState(false);
  const [createAgentModalOpen, setCreateAgentModalOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);
  const [assignSlabModalOpen, setAssignSlabModalOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  
  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
    parent_agent_id: userInfo?.uid,
    'createdAt[gte]': null,
    'createdAt[lte]': null,
    status: null,
    accountType: null,
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["downlineAgents", filter, refetch],
    queryFn: () => getDownlineAgents(userInfo?.uid, filter),
  });

  const handleRefetch = () => {
    setRefetch((prev) => !prev);
  };
  
  const handleSearchChange = (value) => {
    setFilter((prev) => ({
      ...prev,
      search: value,
      page: 1,
    }));
  };
  
  const closeCreateAgentModal = () => {
    setCreateAgentModalOpen(false);
  };

  const openDeleteConfirmation = (agent) => {
    setAgentToDelete(agent);
    setDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
    setAgentToDelete(null);
    setIsDeleting(false);
  };

  const handleDeleteAgent = async () => {
    if (!agentToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteDownlineAgent(agentToDelete.user.uid);
      toast.success(t('agentManagement.deleteSuccess'));
      handleRefetch();
      closeDeleteConfirmation();
    } catch (error) {
      toast.error(t('agentManagement.deleteError'));
      console.error("Delete error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAssignSlab = (agent) => {
    setSelectedAgent(agent);
    setAssignSlabModalOpen(true);
  };

  const handleCloseModals = () => {
    setAssignSlabModalOpen(false);
    setSelectedAgent(null);
  };

  const handleDateRangeApply = (startDate, endDate) => {
    setFilter(prev => ({
      ...prev,
      'createdAt[gte]': startDate,
      'createdAt[lte]': endDate,
      page: 1,
    }));
    setIsDatePickerOpen(false);
  };

  const clearDateFilter = () => {
    setFilter(prev => {
      const newFilter = { ...prev };
      delete newFilter['createdAt[gte]'];
      delete newFilter['createdAt[lte]'];
      return newFilter;
    });
  };

  const formatDateRange = () => {
    if (!filter['createdAt[gte]'] || !filter['createdAt[lte]']) return null;
    const start = new Date(filter['createdAt[gte]']);
    const end = new Date(filter['createdAt[lte]']);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const filterConfig = useMemo(() => [
    {
      key: 'status',
      label: t('agentManagement.columns.status'),
      type: 'dropdown',
      options: [
        { value: 'active', label: t('agentManagement.statusOptions.active') },
        { value: 'inactive', label: t('agentManagement.statusOptions.inactive') },
        // { value: 'suspended', label: t('agentManagement.statusOptions.suspended') },
      ],
      multiSelect: false,
    },
    // {
    //   key: 'accountType',
    //   label: t('agentManagement.accountType'),
    //   type: 'dropdown',
    //   options: [
    //     { value: 'merchant', label: t('agentManagement.accountTypeOptions.merchant') },
    //     { value: 'retailer', label: t('agentManagement.accountTypeOptions.retailer') },
    //   ],
    //   multiSelect: false,
    // }
  ], [t]);

  const initialAdvanceValues = useMemo(() => {
    return {
      status: filter.status || '',
      accountType: filter.accountType || '',
    };
  }, [filter]);

  const handleApplyAdvanceFilters = (filterValues) => {
    setFilter(prev => {
      const newFilter = { ...prev, page: 1 };
      
      if (filterValues.status) {
        newFilter.status = filterValues.status;
      } else {
        delete newFilter.status;
      }
      
      if (filterValues.accountType) {
        newFilter.accountType = filterValues.accountType;
      } else {
        delete newFilter.accountType;
      }
      
      return newFilter;
    });
    
    setIsAdvanceFilterOpen(false);
  };

  const clearAllFilters = () => {
    setFilter({
      search: "",
      limit: 10,
      page: 1,
      parent_agent_id: userInfo?.id,
      'createdAt[gte]': null,
      'createdAt[lte]': null,
      status: null,
      accountType: null,
    });
  };

  const isAnyFilterApplied = useMemo(() => {
    return (
      filter.search || 
      filter['createdAt[gte]'] || 
      filter['createdAt[lte]'] || 
      filter.status || 
      filter.accountType
    );
  }, [filter]);


  const getInitials = (user) => {
    if (!user) return "?";
    
    const name = user?.username || user?.email || "";
    const nameParts = name.trim().split(" ");
    
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
    }
    
    if (name.length > 0) {
      return name.charAt(0).toUpperCase();
    }
    
    return "?";
  };


  const getAvatarColor = (user) => {
    if (!user) return "#4F46E5";
    
    const str = user?.uid || user?.username || user?.email || "default";
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const colors = [
      "#4F46E5", 
      "#0EA5E9", 
      "#10B981", 
      "#F59E0B", 
      "#EF4444",
      "#8B5CF6",
      "#EC4899",
      "#14B8A6",
      "#F97316", 
      "#6366F1", 
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  const columns = [
    {
      key: "profile",
      label: t('agentManagement.columns.profile'),
      render: (item) => {
        const user = item?.user;
        const profilePicture = user?.profilePicture;
        const hasImage = profilePicture && profilePicture.trim() !== "";
        const avatarColor = getAvatarColor(user);
        const initials = getInitials(user);

        if (hasImage) {
          return (
            <div className="relative">
              <img
                src={`${import.meta.env.VITE_IMG_BASE_URL}${profilePicture}`}
                alt="profile"
                className="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-gray-700 shadow-sm"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.querySelector('.avatar-fallback').style.display = 'flex';
                }}
              />
              <div 
                className="avatar-fallback hidden w-10 h-10 rounded-full items-center justify-center text-white font-medium text-sm border-2 border-white dark:border-gray-700 shadow-sm"
                style={{ backgroundColor: avatarColor }}
              >
                {initials}
              </div>
            </div>
          );
        }

        return (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm border-2 border-white dark:border-gray-700 shadow-sm"
            style={{ backgroundColor: avatarColor }}
          >
            {initials}
          </div>
        );
      }
    },
    {
      key: "username",
      label: t('agentManagement.columns.username'),
      render: (item) => item?.user?.username || "-"
    },
    {
      key: "email",
      label: t('agentManagement.columns.email'),
      render: (item) => item?.user?.email || "-"
    },
    {
      key: "mobileNumber",
      label: t('agentManagement.columns.mobileNumber'),
      render: (item) => item?.user?.mobileNumber || "-"
    },
    {
      key: "commissionRate",
      label: t('agentManagement.columns.commissionRate'),
      render: (item) => item?.commissionRateDetails?.percentage ? `${parseFloat(item?.commissionRateDetails?.percentage).toFixed(2)}%` : "-"
    },
    {
      key: "currentSlab",
      label: t('agentManagement.columns.currentSlab'),
      render: (item) => item?.commissionRateDetails?.slabTypeDetails?.title || "-"
    },
    {
      key: "address",
      label: t('agentManagement.columns.address'),
      render: (item) => item?.address || "-"
    },
    {
      key: "status",
      label: t('agentManagement.columns.status'),
      render: (item) => (
        <div
          className={`px-5 py-[6px] ${
            item?.user?.status === "active"
              ? "text-green-500 bg-green-100"
              : "text-red-500 bg-red-100"
          } text-sm text-gray-700 dark:text-gray-300 rounded-4xl flex items-center my-2 justify-center`}
        >
          {item?.user?.status || "-"}
        </div>
      )
    },
    {
      key: "createdAt",
      label: t('agentManagement.columns.createdAt'),
      render: (item) => new Date(item?.createdAt).toLocaleDateString() || "-"
    },
    {
      key: "actions",
      label: t('agentManagement.columns.actions'),
      render: (item) => (
        <div className="flex items-center gap-2">
          <button 
            onClick={() => navigate(`/agent-profile/${item.user.uid}`)}
            className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
            title={t('agentManagement.view')}
          >
            <Eye size={18} />
          </button>
          <button 
            onClick={() => navigate(`/edit-agent/${item.user.uid}`)}
            className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
            title={t('agentManagement.edit')}
          >
            <Pencil size={18} />
          </button>
          <button 
            onClick={() => handleAssignSlab(item)}
            className="p-1 text-purple-600 hover:bg-purple-100 rounded transition-colors"
            title={t('agentManagement.assignSlab')}
          >
            <Plus size={18} />
          </button>
          {/* <button 
            onClick={() => openDeleteConfirmation(item)}
            className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
            title={t('agentManagement.delete')}
          >
            <Trash2 size={18} />
          </button> */}
        </div>
      )
    }
  ];

  return (
    <div className="w-full px-5 py-4 min-h-screen h-auto bg-[linear-gradient(135deg,_#EFF2FF_0%,_#FAF5FF_50%,_#FCF3FB_100%)] dark:bg-[linear-gradient(135deg,_#1e293b_0%,_#334155_50%,_#0f172a_100%)]">
      <CreateAgent
        open={createAgentModalOpen}
        onClose={closeCreateAgentModal}
        refetch={handleRefetch}
        closeCreateAgentModal={closeCreateAgentModal}
      />
      
      <AssignSlabModal
        open={assignSlabModalOpen}
        onClose={handleCloseModals}
        refetch={handleRefetch}
        agentData={selectedAgent}
      />
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmationOpen && (
        <div className="fixed inset-0 bg-black/50 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('agentManagement.deleteConfirmTitle')}
              </h3>
              <button
                onClick={closeDeleteConfirmation}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                disabled={isDeleting}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Trash2 size={32} className="text-red-600 dark:text-red-400" />
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 text-center mb-2">
                {t('agentManagement.deleteConfirmMessage')}
              </p>
              
              {agentToDelete && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                      style={{ backgroundColor: getAvatarColor(agentToDelete.user) }}
                    >
                      {getInitials(agentToDelete.user)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {agentToDelete.user.username || "-"}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {agentToDelete.user.email || "-"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                    <p>{t('agentManagement.agentID')}: {agentToDelete.user.uid}</p>
                  </div>
                </div>
              )}
              
              <p className="text-red-600 dark:text-red-400 text-sm text-center mt-4">
                {t('agentManagement.deleteWarning')}
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={closeDeleteConfirmation}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {t('agentManagement.cancel')}
              </button>
              <button
                onClick={handleDeleteAgent}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t('agentManagement.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    {t('agentManagement.delete')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="w-full flex justify-between flex-col md:flex-row items-start md:items-center gap-5 mt-5">
        <div className="flex gap-3 w-[70%]">
          <div className="w-full md:max-w-[500px] relative">
            <div className="relative bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] dark:from-slate-800 dark:text-white dark:to-slate-800 rounded-md border border-[#E4E7EC] dark:border-gray-500">
              <input
                onChange={(e) => handleSetSearch(handleSearchChange, e.target.value, searchRef)}
                type="text"
                placeholder={t('agentManagement.searchPlaceholder')}
                value={filter.search}
                className="bg-transparent dark:placeholder-gray-200 h-[44px] pl-10 pr-8 transition-all duration-300 w-full outline-none px-3 py-2"
              />
              <Search
                size={18}
                className="absolute top-[12px] left-[8px] text-[#667085] dark:text-gray-300"
              />
              {filter.search && (
                <button
                  onClick={() => setFilter(prev => ({ ...prev, search: "" }))}
                  className="absolute top-[12px] right-[8px] text-[#667085] dark:text-gray-300 hover:text-red-500"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          
          <button
            onClick={() => setIsDatePickerOpen(true)}
            className="max-w-[250px] cursor-pointer w-full md:w-auto border border-[#E4E7EC] rounded-md dark:border-gray-500 px-3 py-2 h-[44px] flex items-center justify-center gap-2 bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] dark:from-slate-800 dark:text-white dark:to-slate-800 relative"
          >
            <ListFilter size={20} className="dark:text-gray-300 text-[#667085]" />
            <p className="dark:text-gray-300 text-[#667085] text-[14px] truncate">
              {filter['createdAt[gte]'] && filter['createdAt[lte]'] 
                ? <span className="pr-5">{formatDateRange()} </span> 
                : t('agentManagement.dateFilter')}
            </p>
            
            {filter['createdAt[gte]'] && filter['createdAt[lte]'] && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  clearDateFilter();
                }}
                className="absolute right-2 text-blue-800 dark:text-blue-200 hover:text-red-500"
              >
                <X size={16} />
              </button>
            )}
          </button>
          
          <button
            onClick={() => setIsAdvanceFilterOpen(true)}
            className="max-w-[250px] cursor-pointer w-full md:w-auto border border-[#E4E7EC] rounded-md dark:border-gray-500 px-3 py-2 h-[44px] flex items-center justify-center gap-2 bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] dark:from-slate-800  dark:text-gray-300 text-[#667085] text-[14px]  dark:to-slate-800 relative"
          >
              <Filter size={20} className="dark:text-gray-300 text-[#667085]" />
              {t('agentManagement.advanceFilter')}
            {isAnyFilterApplied && (
              <span className="absolute top-[-6px] right-[-6px] w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                <span className="text-white text-[10px]">!</span>
              </span>
            )}
          </button>
          
          {isAnyFilterApplied && (
            <button
              onClick={clearAllFilters}
              className="max-w-[150px] cursor-pointer w-full md:w-auto border border-red-500 rounded-md px-3 py-2 h-[44px] flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
            >
              {t('agentManagement.clearAll')}
            </button>
          )}
        </div>
        
        <div className="flex items-center justify-end w-full md:w-auto mt-4 md:mt-0">
          <button
            onClick={() => setCreateAgentModalOpen(true)}
            className="bg-[#CD0C02] px-4 py-[10px] rounded-md text-white flex items-center gap-2 hover:bg-red-700 transition-colors"
          >
            <Plus size={20} />
            {t('agentManagement.createAgent')}
          </button>
        </div>
      </div>

      <div className="mt-5">
        <ARHCustomDataTable
          data={data?.data || []}
          meta={data?.meta || {
            page: filter.page,
            pages: Math.ceil((data?.meta?.totalRecords || 0) / filter.limit),
            total: data?.meta?.totalRecords || 0,
            limit: filter.limit
          }}
          columns={columns}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={handleRefetch}
          onPageChange={(page) => setFilter(prev => ({ ...prev, page }))}
          onLimitChange={(limit) => setFilter(prev => ({ ...prev, limit, page: 1 }))}
          searchQuery={filter.search}
          className="border border-gray-200 rounded-xl"
          rowClassName="hover:bg-gray-50 dark:hover:bg-white/[0.05]"
          emptyState={
            <div className="w-full min-h-[200px] flex flex-col items-center justify-center p-4">
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                <User size={32} className="text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                {filter.search 
                  ? t('agentManagement.noResults', { search: filter.search })
                  : t('agentManagement.noAgents')}
              </p>
            </div>
          }
          errorState={
            <div className="p-4 text-center">
              <div className="text-red-500 mb-2">{t('agentManagement.errorLoading')}</div>
              <button
                onClick={handleRefetch}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                {t('agentManagement.retry')}
              </button>
            </div>
          }
        />
      </div>
      
      {isDatePickerOpen && (
        <div 
          className="fixed inset-0 bg-black/40 bg-opacity-10 z-50 flex items-center justify-center p-4"
          onClick={() => setIsDatePickerOpen(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl max-w-full overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            <ARHCustomDateFiler
              onApply={handleDateRangeApply} 
              onClose={() => setIsDatePickerOpen(false)} 
            />
          </div>
        </div>
      )}
      
      {isAdvanceFilterOpen && (
        <ARHAdvanceFilterModal
          isOpen={isAdvanceFilterOpen}
          onClose={() => setIsAdvanceFilterOpen(false)}
          onApply={handleApplyAdvanceFilters}
          filterConfig={filterConfig}
          initialValues={initialAdvanceValues}
        />
      )}
    </div>
  );
}

export default ManageAgent;